import React, { useState, useEffect, useCallback, useRef } from 'react';
import ClimateMap from './ClimateMap';
import ClimateCharts from './ClimateCharts';
import LocationCreateWizard from './climate/LocationCreateWizard';
import LocationEditManager from './climate/LocationEditManager';
import DataInventory from './climate/DataInventory';
import AddDataPanel from './climate/AddDataPanel';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

/* =========================================================================
   Orchestrator — routes between browse, create, and manage modes
   ========================================================================= */
const ClimateDataView = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'inventory' | 'add' | 'data'

  // Mode: 'browse' | 'create' | 'manage'
  const [mode, setMode] = useState('browse');

  // Bumped whenever stored data changes, to force inventory/panel refetch
  const [dataVersion, setDataVersion] = useState(0);
  // Source the user clicked on the map — pre-opened in the Add / Download tab
  const [focusSourceId, setFocusSourceId] = useState(null);

  // Fetch helpers
  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/locations`);
      if (res.ok) setLocations(await res.json());
    } catch (e) { /* backend not running */ }
  }, []);

  const fetchDatasets = useCallback(async (locId) => {
    try {
      const res = await fetch(`${API}/api/locations/${locId}/datasets`);
      if (res.ok) {
        const json = await res.json();
        setDatasets(json);
        if (json.length > 0) setSelectedDatasetId(json[0].id);
      }
    } catch (e) { console.error('Failed to fetch datasets:', e); }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  useEffect(() => {
    if (selectedLocationId) {
      const loc = locations.find(l => l.id === selectedLocationId);
      setSelectedLocation(loc || null);
      fetchDatasets(selectedLocationId);
      setSelectedDatasetId(null);
      setFocusSourceId(null);
    } else {
      setSelectedLocation(null);
      setDatasets([]);
      setSelectedDatasetId(null);
      setFocusSourceId(null);
    }
  }, [selectedLocationId, locations, fetchDatasets]);

  // Handlers
  const handleStartCreate = () => {
    setMode('create');
    setSelectedLocationId(null);
  };

  const handleCreateComplete = (newLocId) => {
    setMode('browse');
    fetchLocations();
    setSelectedLocationId(newLocId);
    setViewMode('inventory');
  };

  const handleStartManage = (loc) => {
    setSelectedLocationId(loc.id);
    setMode('manage');
  };

  const handleLocationUpdated = () => {
    fetchLocations();
    if (selectedLocationId) fetchDatasets(selectedLocationId);
  };

  const handleDeleteLocation = async (locId) => {
    if (!window.confirm('Delete this location and all its climate datasets?')) return;
    try {
      await fetch(`${API}/api/locations/${locId}`, { method: 'DELETE' });
      setSelectedLocationId(null);
      setMode('browse');
      fetchLocations();
    } catch (e) { console.error('Failed to delete:', e); }
  };

  // Compute sources for map view (from backend).
  // Availability is probed live against PVGIS/Open-Meteo/CAMS and can take several
  // seconds, so the previous location's markers must be cleared immediately and
  // out-of-order responses discarded — otherwise the map keeps showing the sources
  // of whichever location was selected before.
  const [mapSources, setMapSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState(null);
  const sourcesReqRef = useRef(0);

  // Keyed on primitives, not the location object: `locations` is refetched after
  // every mutation, which hands back a new object identity for the same place —
  // depending on the object re-probed (and blanked) the map on every refresh.
  const locLat = selectedLocation?.latitude;
  const locLon = selectedLocation?.longitude;

  useEffect(() => {
    const reqId = ++sourcesReqRef.current;
    setMapSources([]);
    setSourcesError(null);

    if (locLat == null || locLon == null) { setSourcesLoading(false); return; }

    setSourcesLoading(true);
    const latitude = locLat, longitude = locLon;
    fetch(`${API}/api/sources/availability?lat=${latitude}&lon=${longitude}`)
      .then(async r => {
        if (!r.ok) throw new Error(`Source availability failed (HTTP ${r.status})`);
        return r.json();
      })
      .then(data => {
        if (reqId !== sourcesReqRef.current) return;   // a newer location won
        setMapSources((data?.sources || []).filter(s => s.integrated));
        setSourcesLoading(false);
      })
      .catch(e => {
        if (reqId !== sourcesReqRef.current) return;
        setSourcesError(e.message);
        setSourcesLoading(false);
      });
  }, [locLat, locLon]);

  // Data mutated (download / delete) — refresh dataset list, badges and inventory
  const handleDataChanged = useCallback(() => {
    fetchLocations();
    if (selectedLocationId) fetchDatasets(selectedLocationId);
    setDataVersion(v => v + 1);
  }, [fetchLocations, fetchDatasets, selectedLocationId]);

  // Which upstream sources already have data stored for this location.
  // `source` is a label that may join several ids with '+', and source_details
  // carries the authoritative per-variable mapping.
  const usedSourceIds = React.useMemo(() => {
    const ids = new Set();
    datasets.forEach(ds => {
      if (ds.source) String(ds.source).split('+').forEach(s => ids.add(s.trim()));
      const vs = ds.source_details?.variable_sources;
      if (vs) Object.values(vs).forEach(s => ids.add(s));
    });
    return [...ids];
  }, [datasets]);

  const handleSourceSelect = useCallback((src) => {
    setFocusSourceId(src.id);
    setViewMode('add');
  }, []);

  const handleDeleteDataset = async (dsId) => {
    if (!window.confirm('Delete this dataset and all its stored hours?')) return;
    try {
      const res = await fetch(`${API}/api/datasets/${dsId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (selectedDatasetId === dsId) setSelectedDatasetId(null);
      handleDataChanged();
    } catch (e) { console.error('Failed to delete dataset:', e); }
  };

  return (
    <div className="climate-view">
      {/* ===== Left panel: Location list ===== */}
      <div className="climate-left">
        <div className="climate-left-header">
          <h3>Locations</h3>
          <button className="btn-small btn-green" onClick={handleStartCreate}>+ Add</button>
        </div>
        <div className="location-list">
          {locations.map(loc => (
            <div
              key={loc.id}
              className={`location-item ${selectedLocationId === loc.id ? 'active' : ''}`}
              onClick={() => { if (mode === 'browse') setSelectedLocationId(loc.id); }}
              style={mode !== 'browse' ? { opacity: 0.6 } : {}}
            >
              <div className="location-item-header">
                <span className="location-code-badge">{loc.location_code}</span>
                <span className="location-name">{loc.name}</span>
                <div className="location-actions">
                  <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleStartManage(loc); }} title="Manage">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button className="btn-icon btn-danger-icon" onClick={(e) => { e.stopPropagation(); handleDeleteLocation(loc.id); }} title="Delete">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>
              <div className="location-meta">
                {loc.latitude?.toFixed(4)}, {loc.longitude?.toFixed(4)}
                {loc.country && ` \u00B7 ${loc.country}`}
              </div>
              <div className="location-badges">
                {loc.dataset_count > 0 && (
                  <span className="badge badge-green">{loc.dataset_count} dataset{loc.dataset_count !== 1 ? 's' : ''}</span>
                )}
                {loc.project_count > 0 && (
                  <span className="badge badge-blue">{loc.project_count} project{loc.project_count !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
          ))}
          {locations.length === 0 && mode === 'browse' && (
            <div className="location-empty">No locations yet. Click "+ Add" to create one.</div>
          )}
        </div>
      </div>

      {/* ===== Main content ===== */}
      <div className="climate-main">

        {/* --- Create mode --- */}
        {mode === 'create' && (
          <LocationCreateWizard
            onComplete={handleCreateComplete}
            onCancel={() => setMode('browse')}
          />
        )}

        {/* --- Manage mode --- */}
        {mode === 'manage' && selectedLocation && (
          <LocationEditManager
            location={selectedLocation}
            datasets={datasets}
            onLocationUpdated={handleLocationUpdated}
            onCancel={() => setMode('browse')}
          />
        )}

        {/* --- Browse mode --- */}
        {mode === 'browse' && (
          <>
            {/* Toolbar */}
            <div className="climate-toolbar">
              <div className="climate-toolbar-tabs">
                <button className={`toolbar-tab ${viewMode === 'map' ? 'active' : ''}`} onClick={() => setViewMode('map')}>
                  Map &amp; Sources
                </button>
                <button
                  className={`toolbar-tab ${viewMode === 'inventory' ? 'active' : ''}`}
                  onClick={() => setViewMode('inventory')}
                  disabled={!selectedLocation}
                >
                  Stored Data{datasets.length > 0 ? ` (${datasets.length})` : ''}
                </button>
                <button
                  className={`toolbar-tab ${viewMode === 'add' ? 'active' : ''}`}
                  onClick={() => { setFocusSourceId(null); setViewMode('add'); }}
                  disabled={!selectedLocation}
                >
                  Add / Download
                </button>
                <button
                  className={`toolbar-tab ${viewMode === 'data' ? 'active' : ''}`}
                  onClick={() => setViewMode('data')}
                  disabled={datasets.length === 0}
                >
                  Charts
                </button>
              </div>
              {selectedLocation && (
                <div className="climate-toolbar-context">
                  {selectedLocation.location_code} &middot; {selectedLocation.name}
                </div>
              )}
            </div>

            {!selectedLocation && (
              <div className="climate-hint">
                Select a location on the left to see what climate data is stored for it and to download more.
              </div>
            )}

            {/* Map view */}
            {viewMode === 'map' && (
              <div className="climate-map-area">
                <ClimateMap
                  key={selectedLocation ? selectedLocation.id : 'no-location'}
                  location={selectedLocation || { latitude: -28.5, longitude: 25.5 }}
                  sources={selectedLocation ? mapSources : []}
                  onMapClick={() => {}}
                  onSourceSelect={handleSourceSelect}
                  selectedSources={usedSourceIds}
                  pickMode={false}
                />
                {selectedLocation && sourcesLoading && (
                  <div className="sources-legend">
                    <div className="legend-title">Climate Data Sources</div>
                    <div className="legend-item">Probing upstream archives&hellip;</div>
                  </div>
                )}
                {selectedLocation && sourcesError && (
                  <div className="sources-legend">
                    <div className="legend-title">Climate Data Sources</div>
                    <div className="legend-item" style={{ color: '#f87171' }}>{sourcesError}</div>
                  </div>
                )}
                {selectedLocation && !sourcesLoading && mapSources.length > 0 && (
                  <div className="sources-legend">
                    <div className="legend-title">Climate Data Sources</div>
                    {mapSources.map(src => (
                      <div
                        key={src.id}
                        className="legend-item legend-item-clickable"
                        onClick={() => handleSourceSelect(src)}
                        title={`Configure & download ${src.name}`}
                      >
                        <span className="legend-dot" style={{ background: src.color }} />
                        <span className="legend-name">{src.name}</span>
                        <span className="legend-dist">{src.distance_km} km</span>
                        <span className="legend-vars">{src.year_min}&ndash;{src.year_max}</span>
                        <span className="legend-state">
                          {usedSourceIds.includes(src.id) ? 'stored' : 'available'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Stored data — what is in the database, with delete */}
            {viewMode === 'inventory' && selectedLocation && (
              <div className="climate-panel-area">
                <DataInventory locationId={selectedLocation.id} refreshKey={dataVersion} />

                <div className="climate-panel-block">
                  <div className="climate-panel-heading">Stored datasets</div>
                  {datasets.length === 0 && (
                    <div className="climate-hint">
                      Nothing stored for this location yet. Open <b>Add / Download</b> to fetch a source.
                    </div>
                  )}
                  {datasets.length > 0 && (
                    <table className="climate-dataset-table">
                      <thead>
                        <tr>
                          <th>Source</th><th>Years</th><th>Hours</th>
                          <th>Variables</th><th>Status</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        {datasets.map(ds => (
                          <tr key={ds.id}>
                            <td>{ds.source}</td>
                            <td>{ds.year_start ? `${ds.year_start}–${ds.year_end}` : '—'}</td>
                            <td>{(ds.row_count || 0).toLocaleString()}</td>
                            <td className="climate-cell-vars">{(ds.variables || []).join(', ') || '—'}</td>
                            <td className={ds.status === 'complete' ? 'status-ok' : 'status-warn'}>
                              {ds.status}
                              {ds.error_message ? ` — ${ds.error_message}` : ''}
                            </td>
                            <td>
                              <button className="btn-small btn-danger" onClick={() => handleDeleteDataset(ds.id)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* Add / download */}
            {viewMode === 'add' && selectedLocation && (
              <div className="climate-panel-area">
                <AddDataPanel
                  locationId={selectedLocation.id}
                  latitude={selectedLocation.latitude}
                  longitude={selectedLocation.longitude}
                  focusSourceId={focusSourceId}
                  refreshKey={dataVersion}
                  onDataAdded={handleDataChanged}
                />
              </div>
            )}

            {/* Data view */}
            {viewMode === 'data' && (
              <div className="climate-data-area">
                <div className="dataset-selector">
                  {datasets.map(ds => (
                    <button
                      key={ds.id}
                      className={`dataset-chip ${selectedDatasetId === ds.id ? 'active' : ''}`}
                      onClick={() => setSelectedDatasetId(ds.id)}
                    >
                      <span className="dataset-chip-source">{ds.source}</span>
                      <span className="dataset-chip-meta">
                        {ds.row_count?.toLocaleString()} hrs
                        {ds.year_start && ` \u00B7 ${ds.year_start}-${ds.year_end}`}
                      </span>
                    </button>
                  ))}
                </div>
                <ClimateCharts datasetId={selectedDatasetId} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClimateDataView;
